import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { BrandMark } from '../components/BrandMark';
import { BRAND } from '../lib/brand';
import { describeExpiry } from '../lib/shares';
import { format, useTranslation } from '../i18n';

// Public viewer for /s/<token>: no sign-in, no app chrome. Resolves the
// token through the server, which presigns the R2 objects for the share's
// remaining lifetime, then plays them in a grid (`columns` from the share,
// else fit-to-viewport). One native player per clip, nothing preloaded
// beyond metadata, no autoplay: a page of a dozen videos must stay light.

type Item = { id: number; title: string; url: string; content_type: string; size_bytes: number };
type Share = { kind: 'video' | 'group'; title: string; columns: number | null; expires_at: string | null; items: Item[] };
type State = { status: 'loading' } | { status: 'ready'; share: Share } | { status: 'missing' } | { status: 'expired' } | { status: 'error'; message: string };

export default function SharedView() {
  const t = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(`/api/v1/share/${encodeURIComponent(token)}`, { cache: 'no-store' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) return setState({ status: 'missing' });
        if (res.status === 410) return setState({ status: 'expired' });
        if (!res.ok) return setState({ status: 'error', message: format(t.sharedView.requestFailed, { status: res.status }) });
        const share = (await res.json()) as Share;
        setState({ status: 'ready', share });
        document.title = `${share.title} · ${BRAND.name}`;
      })
      .catch((err) => !cancelled && setState({ status: 'error', message: err instanceof Error ? err.message : String(err) }));
    return () => {
      cancelled = true;
      document.title = BRAND.name;
    };
  }, [token]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Container maxWidth={false} sx={{ maxWidth: 1600, py: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <BrandMark size={16} />
          {state.status === 'ready' && (
            <Typography variant="caption" color="text.secondary">
              {state.share.expires_at ? format(t.sharedView.linkExpires, { when: describeExpiry(state.share.expires_at, t.videoShares.expiry) }) : t.sharedView.sharedLink}
            </Typography>
          )}
        </Stack>

        {state.status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        )}
        {state.status === 'missing' && <Notice title={t.sharedView.missingTitle} body={t.sharedView.missingBody} />}
        {state.status === 'expired' && <Notice title={t.sharedView.expiredTitle} body={t.sharedView.expiredBody} />}
        {state.status === 'error' && <Notice title={t.sharedView.errorTitle} body={state.message} />}
        {state.status === 'ready' && <ShareBody share={state.share} />}
      </Container>
    </Box>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 4, maxWidth: 520, mx: 'auto', mt: 8, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Paper>
  );
}

function ShareBody({ share }: { share: Share }) {
  const t = useTranslation();
  const single = share.items.length === 1;
  const columns = single ? 1 : share.columns;
  const gridTemplateColumns = columns ? `repeat(${columns}, minmax(0, 1fr))` : 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))';

  return (
    <Box sx={{ maxWidth: single ? 1100 : 'none', mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: 20, md: 24 } }}>
        {share.title}
      </Typography>
      {share.items.length === 0 ? (
        <Notice title={t.sharedView.emptyTitle} body={t.sharedView.emptyBody} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns, gap: { xs: 1.5, md: 2 } }} data-testid="share-grid">
          {share.items.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
              <Box
                component="video"
                src={item.url}
                controls
                playsInline
                preload="metadata"
                sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', bgcolor: '#000', objectFit: 'contain' }}
              />
              {!single && (
                <Typography variant="body2" sx={{ px: 1.5, py: 1, fontWeight: 500 }} noWrap title={item.title}>
                  {item.title}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
